import type { TFunction } from 'i18next';
import { get } from 'lodash';

import { formatInitCommandDisplay } from '@/delivery-type-plugin/capabilities/command-args-normal';
import type {
  MaasGroupSpec,
  MaasGroupedWorkloadSpec,
  MaasModelSource,
  MaasWorkload,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  MAAS_WORKLOAD_FRAMEWORK_LABEL,
  MAAS_WORKLOAD_RUN_MODE_LABEL,
  MaasModelSourceType,
  MaasRunMode,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { isExistingPvcModelSource } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelSourcePlaceholder';
import {
  formatGroupSystemCommandArgsDisplay,
  formatStartupInitCommandDisplay,
  hasSystemCommandArgsContent,
  isScriptWrappedInitCommand,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/initCommandStartup';

const SOURCE_KEYS: MaasModelSourceType[] = [
  MaasModelSourceType.Http,
  MaasModelSourceType.S3,
  MaasModelSourceType.ModelScope,
  MaasModelSourceType.HuggingFace,
  MaasModelSourceType.StackScope,
  MaasModelSourceType.ModelPlaza,
];

/** 推理镜像展示：从 _inferenceSystem 解析，优先镜像地址，辅以镜像名称（imageKey / 制品名） */
export function getMaasGroupImageDisplay(inferenceSystem?: Record<string, unknown>): string {
  const imageUrl = String(inferenceSystem?.image || inferenceSystem?.url || '').trim();
  const imageName = String(inferenceSystem?.imageName ?? inferenceSystem?.imageKey ?? '').trim();
  return imageUrl || imageName || '-';
}

/** 节点组卡片副标题：初始化命令与参数拼接为完整命令行 */
export function getMaasGroupInitCommandDisplay(spec?: MaasGroupSpec): string {
  if (!spec?.initCommand) return '-';
  if (isScriptWrappedInitCommand(spec.initCommand)) {
    return formatInitCommandDisplay(spec.initCommand);
  }
  return formatStartupInitCommandDisplay(spec.initCommand);
}

/** 节点组卡片副标题：启动命令与参数（system.command/args） */
export function getMaasGroupStartupCommandDisplay(spec?: MaasGroupSpec): string {
  const system = spec?.system as Record<string, unknown> | undefined;
  if (!hasSystemCommandArgsContent(system)) return '-';
  return formatGroupSystemCommandArgsDisplay(system);
}

/** 解析当前生效的模型来源类型 */
export function getActiveModelSourceType(source?: MaasModelSource): MaasModelSourceType | undefined {
  if (!source) return undefined;
  return SOURCE_KEYS.find((key) => {
    const block = source[key];
    if (!block) return false;
    if (typeof block === 'object' && Object.keys(block).length === 0) return false;
    return true;
  });
}

/** 模型来源展示摘要 */
export function getModelSourceSummary(source: MaasModelSource | undefined, t: TFunction, modelName?: string): string {
  if (isExistingPvcModelSource(source, modelName)) {
    return t('data-center:maas.model.source.existing.pvc');
  }
  const type = getActiveModelSourceType(source);
  if (!type) return '-';
  const label = type === MaasModelSourceType.ModelPlaza ? t('home:market.model.repository') : t(`data-center:maas.model.source.${type}`);
  const block = source?.[type];
  if (!block || typeof block !== 'object') return label;
  if (type === MaasModelSourceType.StackScope || type === MaasModelSourceType.ModelPlaza) {
    const model = String(get(block, 'model') || '').trim();
    const url = String(get(block, 'url') || '').trim();
    const detail = [model, url].filter(Boolean).join(' · ');
    return detail ? `${label}: ${detail}` : label;
  }
  const detail = get(block, 'url') || get(block, 'model') || get(block, 'secretRef.accessKeyID');
  return detail ? `${label}: ${detail}` : label;
}

/** 内网 IP 列表文案 */
export function formatMaasInternalIPs(workload?: MaasWorkload): string {
  const ips = get(workload, 'status.network.internalIPs', []) as string[];
  if (!ips.length) return '-';
  return ips.join(', ');
}

/** 从 API Spec 读取 Head 组 */
export function getApiHeadGroupSpec(spec?: MaasGroupedWorkloadSpec): MaasGroupSpec | undefined {
  return spec?.headGroupSpec;
}

/** 从详情 metadata.labels 读取推理框架 */
export function getFrameworkFromWorkload(workload?: MaasWorkload): string | undefined {
  return workload?.metadata?.labels?.[MAAS_WORKLOAD_FRAMEWORK_LABEL];
}

/** 从详情数据读取部署模式 */
export function resolveRunModeFromWorkload(workload?: MaasWorkload): MaasRunMode {
  const labels = workload?.metadata?.labels;
  const spec = workload?.spec;
  const fromLabel = labels?.[MAAS_WORKLOAD_RUN_MODE_LABEL] as MaasRunMode | undefined;
  if (fromLabel === MaasRunMode.PrimarySecondary) {
    return fromLabel;
  }
  if (fromLabel === MaasRunMode.Single || fromLabel === MaasRunMode.Distributed || fromLabel === MaasRunMode.Normal) {
    return MaasRunMode.Normal;
  }
  const head = getApiHeadGroupSpec(spec);
  if (head && (spec?.workerGroupSpecs?.length || 0) > 0) return MaasRunMode.PrimarySecondary;
  return MaasRunMode.Normal;
}

/** Worker 组数量 */
export function getWorkerGroupCount(workload?: MaasWorkload): number {
  const mode = resolveRunModeFromWorkload(workload);
  const count = workload?.spec?.workerGroupSpecs?.length || 0;
  if (mode === MaasRunMode.Normal || mode === MaasRunMode.PrimarySecondary) return count;
  return 0;
}

/** Master + Worker 总副本数 */
export function getTotalReplicas(workload?: MaasWorkload): number {
  const spec = workload?.spec;
  const mode = resolveRunModeFromWorkload(workload);
  if (mode === MaasRunMode.PrimarySecondary) {
    const head = getApiHeadGroupSpec(spec)?.replicas ?? 1;
    const workers = (spec?.workerGroupSpecs || []).reduce((sum, g) => sum + (g.replicas ?? 1), 0);
    return head + workers;
  }
  if (mode === MaasRunMode.Normal) {
    return (spec?.workerGroupSpecs || []).reduce((sum, g) => sum + (g.replicas ?? 1), 0);
  }
  return 0;
}

/** 节点组概览文案 */
export function getDeployNodesSummary(workload: MaasWorkload | undefined, t: TFunction): string {
  if (!workload) return '-';
  const mode = resolveRunModeFromWorkload(workload);
  const workerCount = getWorkerGroupCount(workload);
  if (mode === MaasRunMode.Normal) {
    const workers = workload.spec?.workerGroupSpecs || [];
    if (workers.length === 1 && (workers[0]?.replicas ?? 1) <= 1) {
      return t('data-center:maas.deploy.single.node');
    }
    if (workers.length === 1) {
      const replicas = workers[0]?.replicas ?? 1;
      return t('data-center:maas.deploy.distributed.replicas', { count: replicas });
    }
    return t('data-center:maas.deploy.workers.only', { count: workerCount });
  }
  if (workerCount > 0) {
    return t('data-center:maas.deploy.nodes.summary', { worker: workerCount });
  }
  return t('data-center:maas.deploy.master.only');
}
